import type { Serialised } from '@squaredup/ids';
import type { ProjectedPlugin } from 'dynamo-wrapper';
import { useDataStreamDefinitionsForWorkspace } from 'queries/hooks/useDataStreamDefinitions';
import { datasourceQueryKeys } from 'queries/queryKeys/datasourceKeys';
import { dataStreamDefinitionQueryKeys } from 'queries/queryKeys/dataStreamDefinitionKeys';
import { useMutation, useQueryClient, type UseMutationOptions } from 'react-query';
import { Delete as deleteDataStream } from 'services/DataStreamDefinitionService';
import { Delete as deletePlugin } from 'services/PluginService';
import type { DataStreamId } from 'ui/editor/dataStream/TileEditor/state/TileEditorState';

export const useDeleteDerivedPluginMutation = (
    options: Omit<UseMutationOptions<void, unknown, { pluginId: string }, unknown>, 'mutationFn' | 'mutationKey'> = {}
) => {
    const queryClient = useQueryClient();
    const { data: dataStreamDefinitions, isLoading: isLoadingDataStreams } = useDataStreamDefinitionsForWorkspace();

    const {
        mutate: deleteDerivedPlugin,
        mutateAsync: deleteDerivedPluginAsync,
        isLoading: isDeletingDerivedPlugin,
        ...mutation
    } = useMutation({
        mutationKey: ['deletePlugin'],
        mutationFn: async ({ pluginId }: { pluginId: string }) => {
            if (dataStreamDefinitions == null || isLoadingDataStreams) {
                throw new Error('Cannot delete plugin while data streams are loading.');
            }

            const pluginDataStreamIds: DataStreamId[] = dataStreamDefinitions
                .filter((dataStream) => dataStream.parentPluginId === pluginId)
                .map((dataStream) => dataStream.id);
            await Promise.all(pluginDataStreamIds.map((dataStreamId) => deleteDataStream(dataStreamId)));

            await deletePlugin(pluginId);

            // Optimistically update the query cache to remove the deleted plugin
            queryClient.setQueryData<Serialised<ProjectedPlugin[]>>(datasourceQueryKeys.list, (existingData) => {
                if (existingData == null) {
                    return [];
                }

                return existingData.filter((p) => p.id !== pluginId);
            });

            queryClient.invalidateQueries(dataStreamDefinitionQueryKeys.all);
            queryClient.invalidateQueries(datasourceQueryKeys.list);
        },
        ...options
    });

    return {
        ...mutation,
        isDeletingDerivedPlugin,
        deleteDerivedPlugin,
        deleteDerivedPluginAsync
    };
};
